import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { colors, DEFAULT_THEME, themeFromConfig } from '../constants/colors';
import { Package, User, LogOut, ChevronDown, Search, MessageCircle, Bell, Store, ChevronRight, X } from 'lucide-react';
import { NotificationBell } from './NotificationBell';
import { AuthPromptModal } from './AuthPromptModal';
import { UserAvatar } from './UserAvatar';
import { useIsMobile } from '../hooks/useIsMobile';
import { getUnreadNotificationCount } from '../api/client';
import { useStreamChat } from '../contexts/StreamContext';

interface HeaderProps {
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}

export function Header({ searchQuery, onSearchChange }: HeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [authModal, setAuthModal] = useState<'signin' | 'signup' | null>(null);
  const [notifCount, setNotifCount] = useState(0);
  const [inboxUnread, setInboxUnread] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const { client: streamClient } = useStreamChat();

  const isDiscoverPage = location.pathname === '/shows';
  const isSeller = user?.isSeller;

  const isDefaultTheme = useMemo(() => {
    const theme = themeFromConfig(user?.themeConfig ?? null);
    return theme.primary === DEFAULT_THEME.primary && theme.background === DEFAULT_THEME.background;
  }, [user?.themeConfig]);

  // Fetch notification count for mobile menu badge
  useEffect(() => {
    if (!isMobile || !isAuthenticated) return;
    getUnreadNotificationCount().then(setNotifCount).catch(() => {});
  }, [isMobile, isAuthenticated]);

  // Track unread DM count, updated live via Stream events. Prefer the
  // per-channel sum (updated synchronously by markRead) over the user-level
  // total_unread_count, which can lag — same trap we hit on mobile where the
  // badge would never decrement.
  useEffect(() => {
    if (!streamClient) { setInboxUnread(0); return; }
    const compute = (): number => {
      const channels = streamClient.activeChannels || {};
      let sum = 0;
      let hasMessaging = false;
      for (const cid of Object.keys(channels)) {
        const ch = channels[cid];
        if (ch?.type === 'messaging') {
          hasMessaging = true;
          sum += ch.countUnread();
        }
      }
      if (hasMessaging) return sum;
      return Number(streamClient.user?.total_unread_count ?? 0);
    };
    const refresh = () => setInboxUnread(compute());
    refresh();
    const subs = [
      streamClient.on('notification.message_new', refresh),
      streamClient.on('notification.mark_read', refresh),
      streamClient.on('notification.added_to_channel', refresh),
      streamClient.on('message.new', refresh),
      streamClient.on('message.read', refresh),
      streamClient.on('connection.recovered', refresh),
    ];
    // Safety-net poll — same rationale as mobile (30s).
    const interval = window.setInterval(refresh, 30000);
    return () => {
      subs.forEach(s => s.unsubscribe());
      window.clearInterval(interval);
    };
  }, [streamClient]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setDropdownOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    setDropdownOpen(false);
    logout();
    navigate('/');
  };

  const handleDropdownItemClick = useCallback((path: string) => {
    setDropdownOpen(false);
    navigate(path);
  }, [navigate]);

  const logoElement = (
    <Link to="/" style={styles.logo}>
      {isDefaultTheme ? (
        <img src="/img/snagg_assets/snagg_combined_color.svg" alt="snagg" style={{ height: isMobile ? 26 : 32, width: 'auto', display: 'block' }} />
      ) : (
        <div
          aria-label="snagg"
          role="img"
          style={{
            height: isMobile ? 26 : 32,
            width: '148px',
            display: 'block',
            backgroundColor: 'var(--color-text)',
            WebkitMaskImage: 'url(/img/snagg_assets/snagg_combined_light.svg)',
            WebkitMaskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskImage: 'url(/img/snagg_assets/snagg_combined_light.svg)',
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
          } as React.CSSProperties}
        />
      )}
    </Link>
  );

  // ── Mobile layout ──────────────────────────────────────────────
  if (isMobile) {
    return (
      <header style={styles.header}>
        <div style={mobileStyles.container}>
          {logoElement}
          <div style={{ flex: 1 }} />
          {isAuthenticated ? (
            <div style={styles.dropdownContainer} ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                style={mobileStyles.avatarPill}
                aria-label="Open account menu"
              >
                <UserAvatar src={user?.avatarUrl} alt={user?.username ?? 'User'} size={30} />
                {notifCount > 0 && <span style={mobileStyles.notifDot} />}
                <ChevronDown size={14} color={colors.gray[700]} />
              </button>

              {dropdownOpen && (
                <>
                  <div style={mobileStyles.scrim} onClick={() => setDropdownOpen(false)} />
                  <div style={mobileStyles.dropdown}>
                    {/* Header */}
                    <div style={mobileStyles.menuHeader}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                        <UserAvatar src={user?.avatarUrl} alt={user?.username ?? 'User'} size={44} />
                        <div style={{ minWidth: 0 }}>
                          <div style={mobileStyles.menuUsername}>{user?.username}</div>
                          <div
                            style={mobileStyles.menuViewProfile}
                            onClick={() => handleDropdownItemClick(`/u/${user?.username}`)}
                          >
                            View profile
                          </div>
                        </div>
                      </div>
                      <button onClick={() => setDropdownOpen(false)} style={mobileStyles.closeBtn} aria-label="Close menu">
                        <X size={18} color={colors.gray[600]} />
                      </button>
                    </div>

                    {/* Seller CTA */}
                    <div style={{ padding: '0 16px 12px' }}>
                      <button
                        style={mobileStyles.sellerCard}
                        onClick={() => handleDropdownItemClick('/sell')}
                      >
                        <div style={mobileStyles.sellerCardIcon}>
                          <Store size={20} color="#fff" />
                        </div>
                        <div style={{ flex: 1, textAlign: 'left' as const }}>
                          <div style={mobileStyles.sellerCardTitle}>{isSeller ? 'Seller Hub' : 'Start Selling'}</div>
                        </div>
                        <ChevronRight size={18} color="rgba(255,255,255,0.85)" />
                      </button>
                    </div>

                    {/* Navigation group */}
                    <div style={mobileStyles.menuGroup}>
                      <MobileMenuRow icon={Bell} label="Notifications" badge={notifCount > 0 ? (notifCount > 99 ? '99+' : String(notifCount)) : undefined} badgeColor={colors.accent.pink} onClick={() => handleDropdownItemClick('/notifications')} />
                      <MobileMenuRow icon={MessageCircle} label="Messages" badge={inboxUnread > 0 ? (inboxUnread > 99 ? '99+' : String(inboxUnread)) : undefined} onClick={() => handleDropdownItemClick('/inbox')} />
                      <MobileMenuRow icon={Package} label="My Orders" onClick={() => handleDropdownItemClick('/orders')} last />
                    </div>

                    {/* Account group */}
                    <div style={mobileStyles.menuGroup}>
                      <MobileMenuRow icon={User} label="Account" onClick={() => handleDropdownItemClick('/account')} />
                      <MobileMenuRow icon={LogOut} label="Log Out" danger onClick={handleLogout} last />
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <button onClick={() => setAuthModal('signin')} style={mobileStyles.signInPill}>
              Sign In
            </button>
          )}
        </div>
        {authModal && (
          <AuthPromptModal
            isOpen={true}
            initialTab={authModal}
            onClose={() => setAuthModal(null)}
            onAuthenticated={() => setAuthModal(null)}
          />
        )}
      </header>
    );
  }

  // ── Desktop layout ─────────────────────────────────────────────
  return (
    <header style={styles.header}>
      <div style={styles.container}>
        {logoElement}

        {/* Search bar - only on discover page */}
        {isDiscoverPage && onSearchChange && (
          <div style={styles.searchContainer}>
            <Search size={18} color={colors.gray[600]} style={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search shows or sellers..."
              value={searchQuery || ''}
              onChange={(e) => onSearchChange(e.target.value)}
              style={styles.searchInput}
            />
          </div>
        )}

        {/* Right side actions */}
        <div style={styles.actions}>
          {isAuthenticated ? (
            <>
              {isSeller ? (
                <Link to="/sell" style={styles.sellerHubLink}>Seller Hub</Link>
              ) : (
                <Link to="/sell" style={styles.startSellingButton}>Start Selling</Link>
              )}
              <Link to="/orders" style={{ color: colors.gray[600], display: 'flex', alignItems: 'center', padding: '6px', textDecoration: 'none' }} title="Orders">
                <Package size={20} />
              </Link>
              <Link to="/inbox" style={{ color: colors.gray[600], display: 'flex', alignItems: 'center', padding: '6px', textDecoration: 'none', position: 'relative' }} title="Messages">
                <MessageCircle size={20} />
                {inboxUnread > 0 && (
                  <span style={{
                    position: 'absolute', top: 0, right: 0,
                    backgroundColor: colors.accent.pink, color: 'white',
                    fontSize: '10px', fontWeight: 700,
                    minWidth: '16px', height: '16px', borderRadius: '8px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 4px',
                  }}>{inboxUnread > 99 ? '99+' : inboxUnread}</span>
                )}
              </Link>
              <NotificationBell />
              <div style={styles.dropdownContainer} ref={dropdownRef}>
                <button onClick={() => setDropdownOpen(!dropdownOpen)} style={styles.userButton}>
                  <UserAvatar src={user?.avatarUrl} alt={user?.username ?? 'User'} size={32} />
                  <span style={styles.username}>{user?.username}</span>
                  <ChevronDown size={16} color={colors.gray[600]} style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                </button>
                {dropdownOpen && (
                  <div style={styles.dropdown}>
                    <button style={styles.dropdownItem} onClick={() => handleDropdownItemClick('/account')}>
                      <User size={18} color={colors.gray[600]} />
                      <span>Account</span>
                    </button>
                    <div style={styles.dropdownDivider} />
                    <button style={styles.dropdownItemLogout} onClick={handleLogout}>
                      <LogOut size={18} color={colors.accent.pink} />
                      <span>Log Out</span>
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={styles.authButtons}>
              <button onClick={() => setAuthModal('signin')} style={styles.loginButton}>Sign In</button>
              <button onClick={() => setAuthModal('signup')} style={styles.registerButton}>Sign Up</button>
            </div>
          )}
        </div>
      </div>
      {authModal && (
        <AuthPromptModal
          isOpen={true}
          initialTab={authModal}
          onClose={() => setAuthModal(null)}
          onAuthenticated={() => setAuthModal(null)}
        />
      )}
    </header>
  );
}

// ── Mobile menu row component ──────────────────────────────────
function MobileMenuRow({ icon: Icon, label, badge, badgeColor, danger, last, onClick }: {
  icon: React.ComponentType<{ size: number; color?: string }>;
  label: string;
  badge?: string;
  badgeColor?: string;
  danger?: boolean;
  last?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        minHeight: 50,
        border: 'none',
        borderBottom: last ? 'none' : `1px solid ${colors.gray[200]}`,
        background: '#fff',
        cursor: 'pointer',
        fontFamily: 'inherit',
        color: danger ? colors.accent.pink : colors.gray[900],
      }}
    >
      <div style={{ width: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', color: danger ? colors.accent.pink : colors.gray[700] }}>
        <Icon size={20} color="currentColor" />
      </div>
      <span style={{ flex: 1, textAlign: 'left' as const, fontSize: 15, fontWeight: 500 }}>{label}</span>
      {badge && (
        <span style={{
          padding: '2px 7px',
          fontSize: 11,
          fontWeight: 700,
          color: '#fff',
          borderRadius: 999,
          background: badgeColor || colors.brand.blue,
        }}>{badge}</span>
      )}
      {!danger && <ChevronRight size={16} color={colors.gray[400]} />}
    </button>
  );
}

// ── Mobile styles ──────────────────────────────────────────────
const mobileStyles: Record<string, React.CSSProperties> = {
  container: {
    height: 56,
    padding: '0 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  signInPill: {
    padding: '9px 20px',
    minHeight: 38,
    background: colors.brand.blue,
    color: '#fff',
    border: 'none',
    borderRadius: 999,
    fontSize: 14,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(95,61,196,0.25)',
  },
  avatarPill: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 8px 3px 3px',
    minHeight: 38,
    background: '#fff',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: 999,
    cursor: 'pointer',
    fontFamily: 'inherit',
    position: 'relative',
  },
  notifDot: {
    position: 'absolute',
    top: 2,
    left: 24,
    width: 10,
    height: 10,
    background: colors.accent.pink,
    borderRadius: '50%',
    border: '2px solid #fff',
  },
  scrim: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    zIndex: 150,
  },
  dropdown: {
    position: 'fixed',
    top: 56,
    right: 8,
    left: 8,
    maxWidth: 340,
    marginLeft: 'auto',
    background: '#fff',
    borderRadius: 18,
    zIndex: 200,
    boxShadow: '0 12px 40px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)',
    paddingBottom: 8,
    maxHeight: 'calc(100dvh - 70px)',
    overflowY: 'auto',
  },
  menuHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '16px 16px 12px',
  },
  menuUsername: {
    fontSize: 17,
    fontWeight: 700,
    color: colors.gray[900],
    letterSpacing: '-0.01em',
  },
  menuViewProfile: {
    fontSize: 12,
    color: colors.gray[600],
    marginTop: 2,
    cursor: 'pointer',
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: 'none',
    background: colors.gray[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  sellerCard: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    background: colors.brand.blue,
    border: 'none',
    borderRadius: 14,
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(95,61,196,0.25)',
    fontFamily: 'inherit',
  },
  sellerCardIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'rgba(255,255,255,0.18)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sellerCardTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#fff',
    letterSpacing: '-0.01em',
  },
  menuGroup: {
    margin: '0 16px 12px',
    background: '#fff',
    borderRadius: 14,
    overflow: 'hidden',
    border: `1px solid ${colors.gray[300]}`,
  },
};

// ── Desktop styles ─────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  header: {
    backgroundColor: colors.white,
    borderBottom: `1px solid ${colors.gray[300]}`,
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
  logo: {
    textDecoration: 'none',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  searchContainer: {
    flex: 1,
    maxWidth: '480px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '12px 12px 12px 40px',
    fontSize: '14px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '10px',
    backgroundColor: colors.gray[100],
    outline: 'none',
    minHeight: '44px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginLeft: 'auto',
  },
  sellerHubLink: {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[700],
    backgroundColor: 'transparent',
    textDecoration: 'none',
    borderRadius: '8px',
    transition: 'background-color 0.2s, color 0.2s',
  },
  startSellingButton: {
    padding: '8px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    textDecoration: 'none',
    borderRadius: '8px',
    transition: 'background-color 0.2s, color 0.2s',
  },
  dropdownContainer: {
    position: 'relative',
  },
  userButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px 6px 6px',
    backgroundColor: colors.gray[100],
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '100px',
    cursor: 'pointer',
    transition: 'background-color 0.2s, color 0.2s',
  },
  username: {
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[900],
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    minWidth: '200px',
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.gray[300]}`,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    zIndex: 1000,
  },
  dropdownItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[900],
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color 0.2s',
  },
  dropdownItemLogout: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.accent.pink,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color 0.2s',
  },
  dropdownDivider: {
    height: '1px',
    backgroundColor: colors.gray[300],
    margin: '4px 0',
  },
  authButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  loginButton: {
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[900],
    background: 'none',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s, color 0.2s',
    minHeight: '44px',
  },
  registerButton: {
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s, color 0.2s',
    minHeight: '44px',
  },
};
